import axios from "axios";
import AuthService from "./AuthService";

const apiClient = axios.create({
  baseURL: "/api",
  withCredentials: false,
});

export default {
  async getTokenHeader() {
    return {
      headers: AuthService.getAuthHeader(),
    };
  },

  // ============================================
  // CHAT / ASSISTANT
  // ============================================

  async start(assistantId, tenantId) {
    return apiClient.get("/chat/start-session", {
      params: { assistant_id: assistantId, tenant_id: tenantId },
    });
  },

  async prompt(sessionId, message, tenantId) {
    return apiClient.post(
      "/chat/prompt",
      { prompt: message },
      { params: { session_id: sessionId, tenant_id: tenantId } }
    );
  },

  // ============================================
  // MESSAGING
  // ============================================

  async sns(subject, message) {
    const header = await this.getTokenHeader();
    return apiClient.post("/message/email", { subject, body: message }, header);
  },

  // ============================================
  // TABLE OPERATIONS
  // ============================================

  async getTables() {
    const header = await this.getTokenHeader();
    return apiClient.get("/table/", header);
  },

  async getTenant(tenantId) {
    const header = await this.getTokenHeader();
    return apiClient.get(`/tenant/${tenantId}`, header);
  },

  async installApp(appName) {
    const header = await this.getTokenHeader();
    return apiClient.post(`/tenant/apps/install/${appName}`, null, header);
  },

  // ============================================
  // VIEW OPERATIONS
  // ============================================

  async getViews() {
    const header = await this.getTokenHeader();
    return apiClient.get("/view/", header);
  },

  async getViewData(viewName, skip = 0, limit = 100) {
    const header = await this.getTokenHeader();
    return apiClient.get(`/data/view/${viewName}`, {
      ...header,
      params: { skip, limit },
    });
  },

  async queryViewData(viewName, filters, skip = 0, limit = 100) {
    const header = await this.getTokenHeader();
    return apiClient.post(
      `/data/view/${viewName}/query`,
      { filters, skip, limit },
      header
    );
  },

  // ============================================
  // DATA OPERATIONS (authenticated)
  // ============================================

  async createRecord(tableName, recordData) {
    const header = await this.getTokenHeader();
    return apiClient.post(`/data/table/${tableName}`, recordData, header);
  },

  async getRecords(tableName, skip = 0, limit = 100) {
    const header = await this.getTokenHeader();
    return apiClient.get(`/data/table/${tableName}`, {
      ...header,
      params: { skip, limit },
    });
  },

  async queryRecords(tableName, filters, skip = 0, limit = 100) {
    const header = await this.getTokenHeader();
    return apiClient.post(
      `/data/table/${tableName}/query`,
      { filters, skip, limit },
      header
    );
  },

  async getRecord(tableName, recordId) {
    const header = await this.getTokenHeader();
    return apiClient.get(`/data/table/${tableName}/${recordId}`, header);
  },

  async updateRecord(tableName, recordId, recordData) {
    const header = await this.getTokenHeader();
    return apiClient.put(
      `/data/table/${tableName}/${recordId}`,
      recordData,
      header
    );
  },

  async deleteRecord(tableName, recordId) {
    const header = await this.getTokenHeader();
    return apiClient.delete(`/data/table/${tableName}/${recordId}`, header);
  },

  async retryRecord(tableName, recordId) {
    const header = await this.getTokenHeader();
    return apiClient.post(
      `/data/table/${tableName}/${recordId}/retry`,
      {},
      header
    );
  },

  // ============================================
  // PUBLIC DATA OPERATIONS (no auth)
  // ============================================

  async getPublicViewData(tenantId, viewName, skip = 0, limit = 100) {
    return apiClient.get(`/public/data/view/${tenantId}/${viewName}`, {
      params: { skip, limit },
    });
  },

  async createPublicRecord(tenantId, tableName, recordData) {
    return apiClient.post(
      `/public/data/table/${tenantId}/${tableName}`,
      recordData
    );
  },
};
